import { Controller } from "@hotwired/stimulus"
import NotificationController from "./notification_controller"

export default class extends Controller {
  connect() {
    // Only override if not already done
    if (!window.__turboConfirmOverridden) {
      this.overrideConfirm()
      window.__turboConfirmOverridden = true
    }
    
    // Also listen for the turbo:confirm event (for newer versions)
    this.handleTurboConfirm = this.handleTurboConfirm.bind(this)
    document.addEventListener("turbo:confirm", this.handleTurboConfirm, { capture: true })
    
  }

  disconnect() {
    // Remove event listener
    document.removeEventListener("turbo:confirm", this.handleTurboConfirm, { capture: true })
  }

  overrideConfirm() {
    // Save original confirm function if not already saved
    if (!window.__originalConfirm) {
      window.__originalConfirm = window.confirm
    }
    
    // Track the last clicked element and if a modal is already showing
    let lastClickedElement = null
    let isModalShowing = false
    
    // Listen for clicks on submit buttons to track which element triggered the confirm
    this.clickHandler = (event) => {
      const element = event.target
      // Check if the element or its parent has data-turbo-confirm or data-confirm
      const confirmElement = 
        (element.dataset?.turboConfirm || element.dataset?.confirm) ? element : 
        element.closest('[data-turbo-confirm], [data-confirm], form[data-turbo-confirm], form[data-confirm]')
      
      if (confirmElement) {
        lastClickedElement = confirmElement
        // Also store the actual button clicked if it's inside a form
        if (element.type === 'submit' && element.form) {
          lastClickedElement.submitter = element
        }
      }
    }
    
    document.addEventListener('click', this.clickHandler, true)
    
    // Override window.confirm which Turbo uses internally
    window.confirm = (message) => {
      // If a modal is already showing, don't show another one
      if (isModalShowing) {
        return false
      }
      
      // Check if it's a delete operation based on message
      const isDelete = message && (
        message.toLowerCase().includes("supprimer") ||
        message.toLowerCase().includes("delete") ||
        message.toLowerCase().includes("remove")
      )
      
      // Get the element that triggered this confirmation
      const triggerElement = lastClickedElement || document.activeElement
      // Check both for a parent form and if the element itself is a form
      const form = triggerElement?.tagName === 'FORM' ? triggerElement : triggerElement?.closest("form")
      
      // Mark that we're showing a modal
      isModalShowing = true
      
      // Show SweetAlert2 and handle the async nature
      setTimeout(async () => {
        try {
          let confirmed
          if (isDelete) {
            // Don't pass the duplicate text, just use the title
            confirmed = await NotificationController.confirmDelete(
              message || "Êtes-vous sûr ?",
              null // Remove the redundant text
            )
          } else {
            confirmed = await NotificationController.confirm(
              message || "Êtes-vous sûr ?",
              null
            )
          }
          
          // Only proceed if user confirmed
          if (confirmed) {
            // Handle different cases: forms, links, buttons
            if (form) {
              // Case 1: Form submission
              const originalConfirm = form.dataset.turboConfirm || triggerElement?.dataset?.turboConfirm
              const submitter = triggerElement?.submitter || triggerElement
              
              if (form.dataset.turboConfirm) {
                delete form.dataset.turboConfirm
              }
              if (triggerElement?.dataset?.turboConfirm) {
                delete triggerElement.dataset.turboConfirm
              }
              
              // Important: Ensure the form has data-turbo="true" for proper Turbo handling
              if (!form.dataset.turbo) {
                form.dataset.turbo = "true"
              }
              
              // Re-submit the form with the correct submitter
              if (submitter && submitter.type === 'submit') {
                form.requestSubmit(submitter)
              } else {
                form.requestSubmit()
              }
              
              // Restore confirmation attribute after a delay
              setTimeout(() => {
                if (originalConfirm) {
                  if (form) form.dataset.turboConfirm = originalConfirm
                  if (triggerElement) triggerElement.dataset.turboConfirm = originalConfirm
                }
              }, 100)
            } else if (triggerElement) {
              // Case 2: Link or button with data-turbo-method or data-method
              const originalConfirm = triggerElement.dataset.confirm || triggerElement.dataset.turboConfirm
              
              // Temporarily remove confirmation to avoid loop
              if (triggerElement.dataset.confirm) {
                delete triggerElement.dataset.confirm
              }
              if (triggerElement.dataset.turboConfirm) {
                delete triggerElement.dataset.turboConfirm
              }
              
              // Re-trigger the click
              triggerElement.click()
              
              // Restore confirmation after a delay
              setTimeout(() => {
                if (originalConfirm) {
                  if (triggerElement.dataset.confirm !== undefined) {
                    triggerElement.dataset.confirm = originalConfirm
                  } else {
                    triggerElement.dataset.turboConfirm = originalConfirm
                  }
                }
              }, 100)
            }
          }
        } finally {
          // Reset flags
          isModalShowing = false
          lastClickedElement = null
        }
      }, 0)
      
      // Always return false initially to cancel the original action
      return false
    }
  }

  async handleTurboConfirm(event) {
    // For newer versions of Turbo that use the event API
    event.preventDefault()
    
    const message = event.detail?.message || "Êtes-vous sûr ?"
    const element = event.target
    
    // Check if it's a delete operation
    const isDelete = element?.dataset?.method === "delete" || 
                    element?.closest("form")?.dataset?.method === "delete" ||
                    message.toLowerCase().includes("supprimer")
    
    let confirmed
    if (isDelete) {
      confirmed = await NotificationController.confirmDelete(
        message,
        "Cette action ne peut pas être annulée."
      )
    } else {
      confirmed = await NotificationController.confirm(message, null)
    }
    
    // Resume or cancel based on confirmation
    if (confirmed && event.detail?.resume) {
      event.detail.resume()
    } else if (confirmed) {
      // For older versions, we need to manually trigger the action
      const form = element.closest("form")
      if (form) {
        // Temporarily remove the data-turbo-confirm to avoid loop
        const originalConfirm = form.dataset.turboConfirm
        delete form.dataset.turboConfirm
        form.requestSubmit()
        // Restore it after a short delay
        setTimeout(() => {
          if (originalConfirm) {
            form.dataset.turboConfirm = originalConfirm
          }
        }, 100)
      }
    }
  }
}